import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import BuildIcon from "@mui/icons-material/Build";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { selectRoles } from "../../../features/roleSlice";
import { IconTile, PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState } from "../../../components/UI/States";
import { checkRoles, matchesQuery } from "../../../components/Navigation/SideNav";
import {
  FORMS_CATALOG,
  MY_SUBMISSIONS_PATH,
} from "../../../components/Forms/formsCatalog";

// Icons live here, not in the catalog, so the catalog stays plain data.
const FORM_ICONS = {
  rfq: RequestQuoteIcon,
  "service-request": BuildIcon,
  remodel: HomeWorkIcon,
  "order-problem": ReportProblemIcon,
  recognition: EmojiEventsIcon,
  "feature-request": LightbulbIcon,
};

// Icon tile tint per form — matches each form's own page header.
const FORM_TONES = {
  rfq: "sky",
  "service-request": "teal",
  remodel: "orange",
  "order-problem": "rose",
  recognition: "emerald",
  "feature-request": "amber",
};

const FormCard = ({ form }) => {
  const Icon = FORM_ICONS[form.id] || EditNoteIcon;
  return (
    <div className="app-card flex flex-col rounded-2xl border p-5">
      <div className="flex items-center gap-3">
        <IconTile icon={Icon} tone={FORM_TONES[form.id] || "sky"} />
        <h2 className="text-base font-semibold leading-snug">{form.name}</h2>
      </div>
      <p className="app-muted mt-3 flex-1 text-sm leading-relaxed">
        {form.description}
      </p>
      {/* Uniform footer: full-width button, then one small line that either
          links to the tracking page or explains why there isn't one. The
          non-breaking space keeps footers level across the card row. */}
      <div className="app-divider mt-4 border-t pt-4">
        <Button component={Link} to={form.path} variant="contained" fullWidth>
          Open Form
        </Button>
        <p className="mt-2.5 text-center text-xs">
          {form.track ? (
            <Link to={form.track.to} className="app-link">
              Track it under {form.track.label} →
            </Link>
          ) : (
            <span className="app-muted">{form.note || "\u00A0"}</span>
          )}
        </p>
      </div>
    </div>
  );
};

/**
 * The one place to start any form on the site — cards for every submit-form
 * the user can open, with search. New forms register in
 * components/Forms/formsCatalog.js, not in the side nav (see CLAUDE.md).
 */
const FormsHub = () => {
  const role = useSelector(selectRoles);
  const userRoles = useMemo(() => role?.roles || [], [role]);
  const grantedPaths = role?.grantedPaths;
  const [query, setQuery] = useState("");

  const forms = useMemo(() => {
    const visible = FORMS_CATALOG.filter((form) =>
      checkRoles(form.roles, userRoles, form.path, grantedPaths)
    );
    const trimmed = query.trim();
    if (!trimmed) return visible;
    return visible.filter((form) =>
      matchesQuery(
        [form.name, form.description, ...(form.keywords || [])]
          .join(" ")
          .toLowerCase(),
        trimmed
      )
    );
  }, [userRoles, grantedPaths, query]);

  const trimmedQuery = query.trim();

  return (
    <PageShell size="lg">
      <PageHeader
        icon={EditNoteIcon}
        tone="sky"
        eyebrow="Forms"
        title="Submit a Form"
        description="Every form on the site, in one place. Track what you've sent under My Submissions."
        actions={
          <Button
            component={Link}
            to={MY_SUBMISSIONS_PATH}
            variant="outlined"
            startIcon={<FactCheckIcon />}
          >
            My Submissions
          </Button>
        }
      />

      <TextField
        fullWidth
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Find a form… (e.g. quote, remodel, problem, service)"
        inputProps={{ "aria-label": "Find a form" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {forms.length ? (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => (
            <FormCard key={form.id} form={form} />
          ))}
        </div>
      ) : (
        <div className="app-card mt-6 rounded-2xl border">
          <EmptyState
            icon={SearchOffIcon}
            title={`No forms match “${trimmedQuery}”`}
            description="Try a different word — quote, remodel, problem, or service."
            action={
              <Button variant="outlined" onClick={() => setQuery("")}>
                Clear search
              </Button>
            }
          />
        </div>
      )}
    </PageShell>
  );
};

export default FormsHub;
